import { useState } from 'react';
import { Popover } from 'antd';
import { useGateValue } from '@statsig/react-bindings';
import type { ShipmentRow } from '../../../../types';
import AlertActivityPopover from '../AlertActivityPopover/AlertActivityPopover';
import AlertSeverityIcon from '../AlertSeverityIcon/AlertSeverityIcon';
import { formatAlertCount } from '../alertActivityUtils';
import styles from './ShipmentAlertsCell.module.css';

interface ShipmentAlertsCellProps {
  record: ShipmentRow;
  /** What the column showed before alert activity existed. */
  fallbackText: string;
}

/**
 * Alerts column: severity icon plus "N alerts". Hovering or clicking the count
 * opens the alert activity popover. Falls back to the old plain value while the
 * alert_assignment gate is off, for sub-rows, or if the backend has no activity.
 */
const ShipmentAlertsCell = ({
  record,
  fallbackText,
}: ShipmentAlertsCellProps) => {
  const gateEnabled = useGateValue('alert_assignment');
  const [open, setOpen] = useState(false);
  const activity = record.alert_activity;
  const shipmentId = record.shipment_id ?? record.id;

  if (
    !gateEnabled ||
    !activity?.available ||
    !shipmentId ||
    record.leg_type === 'EL'
  ) {
    return <>{fallbackText}</>;
  }

  if (activity.total_alert_count <= 0) return <span>-</span>;

  return (
    <Popover
      arrow={false}
      placement='bottom'
      styles={{
        container: { padding: 0, borderRadius: 12, overflow: 'hidden' },
      }}
      trigger={['hover', 'click']}
      open={open}
      onOpenChange={setOpen}
      content={
        <AlertActivityPopover
          shipmentId={shipmentId}
          activityVersion={activity.activity_version}
          open={open}
        />
      }
    >
      <span
        className={styles.trigger}
        role='button'
        tabIndex={0}
        // Do not let opening the popover also open the shipment drawer.
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
      >
        <AlertSeverityIcon severity={activity.highest_severity} />
        <span className={styles.count}>
          {formatAlertCount(activity.total_alert_count)}
        </span>
      </span>
    </Popover>
  );
};

export default ShipmentAlertsCell;
